import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useTranslation } from '../../../../../i18n';
import {
  InventoryLocation,
  InventoryLocationCreate,
  InventoryLocationUpdate,
  InventoryLocationType,
} from '../../../../../types/api';
import { buildFlatTreeOptions } from './inventoryTree.utils';

const LOCATION_TYPES: InventoryLocationType[] = [
  'area',
  'shelf',
  'level',
  'box',
  'pelicase',
  'bag',
  'section',
  'bucket',
  'container',
];

interface LocationDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: InventoryLocationCreate | InventoryLocationUpdate) => void;
  locations: InventoryLocation[];
  editLocation?: InventoryLocation | null;
  defaultParentId?: string | null;
  t: ReturnType<typeof useTranslation>['t'];
}

export const LocationDialog: React.FC<LocationDialogProps> = ({
  open,
  onClose,
  onSave,
  locations,
  editLocation,
  defaultParentId,
  t,
}) => {
  const [name, setName] = useState('');
  const [locationType, setLocationType] = useState<InventoryLocationType>('area');
  const [parentId, setParentId] = useState<string | ''>('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (open) {
      if (editLocation) {
        setName(editLocation.name);
        setLocationType(editLocation.location_type);
        setParentId(editLocation.parent_id ?? '');
        setNotes(editLocation.notes ?? '');
      } else {
        setName('');
        setLocationType('area');
        setParentId(defaultParentId ?? '');
        setNotes('');
      }
    }
  }, [open, editLocation, defaultParentId]);

  const handleSave = () => {
    const data: InventoryLocationCreate = {
      name: name.trim(),
      location_type: locationType,
      parent_id: parentId || null,
      notes: notes.trim() || null,
    };
    onSave(data);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {editLocation ? t('inventory.editLocation') : t('inventory.addLocation')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label={t('inventory.name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            autoFocus
          />
          <FormControl fullWidth>
            <InputLabel>{t('inventory.locationType')}</InputLabel>
            <Select
              value={locationType}
              label={t('inventory.locationType')}
              onChange={(e) => setLocationType(e.target.value as InventoryLocationType)}
            >
              {LOCATION_TYPES.map((lt) => (
                <MenuItem key={lt} value={lt}>
                  {t(`inventory.locationTypes.${lt}` as Parameters<typeof t>[0])}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>{t('inventory.parentLocation')}</InputLabel>
            <Select
              value={parentId}
              label={t('inventory.parentLocation')}
              onChange={(e) => setParentId(e.target.value)}
            >
              <MenuItem value="">
                <em>None (root)</em>
              </MenuItem>
              {buildFlatTreeOptions(locations, editLocation?.id, t).map((opt) => (
                <MenuItem key={opt.id} value={opt.id} sx={{ pl: 2 + opt.depth * 2 }}>
                  {opt.name}
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{ ml: 1, color: 'text.secondary' }}
                  >
                    ({opt.typeLabel})
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label={t('inventory.notes')}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            rows={2}
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button onClick={handleSave} variant="contained" disabled={!name.trim()}>
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
